import { api } from '../../lib/api';
import { putToStorage } from '../../lib/storage';

export const SUPPLIER_STATUSES = [
  { value: 'Draft', label: 'Draft', tone: 'neutral' },
  { value: 'Active', label: 'Active', tone: 'success' },
  { value: 'OnHold', label: 'On hold', tone: 'warning' },
  { value: 'Archived', label: 'Archived', tone: 'danger' },
];

export const statusMeta = (status) =>
  SUPPLIER_STATUSES.find((s) => s.value === status) || { value: status, label: status, tone: 'neutral' };

export const ADDRESS_TYPES = [
  { value: 'Headquarters', label: 'Headquarters' },
  { value: 'Billing', label: 'Billing' },
  { value: 'Shipping', label: 'Shipping' },
  { value: 'Remittance', label: 'Remittance' },
  { value: 'Warehouse', label: 'Warehouse' },
  { value: 'Other', label: 'Other' },
];

export const DOCUMENT_TYPES = [
  { value: 'Contract', label: 'Contract' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'TaxForm', label: 'Tax form' },
  { value: 'Certification', label: 'Certification' },
  { value: 'License', label: 'License' },
  { value: 'PriceList', label: 'Price list' },
  { value: 'Catalog', label: 'Catalog' },
  { value: 'Other', label: 'Other' },
];

export const documentTypeLabel = (value) =>
  DOCUMENT_TYPES.find((d) => d.value === value)?.label || value;

function toQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const suppliersApi = {
  // Core supplier CRUD
  list: (params = {}) => api.get(`/suppliers${toQuery(params)}`),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (payload) => api.post('/suppliers', payload),
  update: (id, payload) => api.putWithId(`/suppliers/${id}`, id, payload),
  remove: (id) => api.del(`/suppliers/${id}`),
  stats: (id) => api.get(`/suppliers/${id}/stats`),
  spend: (id) => api.get(`/suppliers/${id}/spend`),

  // Contacts (return the full updated supplier)
  addContact: (supplierId, payload) => api.post(`/suppliers/${supplierId}/contacts`, payload),
  updateContact: (supplierId, contactId, payload) => api.put(`/suppliers/${supplierId}/contacts/${contactId}`, payload),
  removeContact: (supplierId, contactId) => api.del(`/suppliers/${supplierId}/contacts/${contactId}`),

  // Addresses (return the full updated supplier)
  addAddress: (supplierId, payload) => api.post(`/suppliers/${supplierId}/addresses`, payload),
  updateAddress: (supplierId, addressId, payload) => api.put(`/suppliers/${supplierId}/addresses/${addressId}`, payload),
  removeAddress: (supplierId, addressId) => api.del(`/suppliers/${supplierId}/addresses/${addressId}`),

  // Bank accounts (return the full updated supplier)
  addBankAccount: (supplierId, payload) => api.post(`/suppliers/${supplierId}/bank-accounts`, payload),
  updateBankAccount: (supplierId, bankAccountId, payload) => api.put(`/suppliers/${supplierId}/bank-accounts/${bankAccountId}`, payload),
  removeBankAccount: (supplierId, bankAccountId) => api.del(`/suppliers/${supplierId}/bank-accounts/${bankAccountId}`),

  // Documents
  listDocuments: (supplierId) => api.get(`/suppliers/${supplierId}/documents`),
  removeDocument: (supplierId, documentId) => api.del(`/suppliers/${supplierId}/documents/${documentId}`),

  /**
   * Uploads a file directly to S3 via a presigned URL, then registers its metadata.
   * `meta`: { documentType, description, expiresAt }
   */
  uploadDocument: async (supplierId, file, meta = {}) => {
    const contentType = file.type || 'application/octet-stream';
    const ticket = await api.post(`/suppliers/${supplierId}/documents/upload-url`, {
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
    });

    await putToStorage(ticket.uploadUrl, file, contentType);

    return api.post(`/suppliers/${supplierId}/documents`, {
      documentId: ticket.documentId,
      storageKey: ticket.storageKey,
      fileName: file.name,
      contentType: file.type || null,
      sizeBytes: file.size,
      documentType: meta.documentType || 'Other',
      description: meta.description || null,
      expiresAt: meta.expiresAt || null,
    });
  },

  /** Resolves a short-lived download URL and opens it in a new tab. */
  openDocument: async (supplierId, documentId) => {
    const res = await api.get(`/suppliers/${supplierId}/documents/${documentId}/download-url`);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
    return res;
  },

  // Notes
  listNotes: (supplierId) => api.get(`/suppliers/${supplierId}/notes`),
  addNote: (supplierId, payload) => api.post(`/suppliers/${supplierId}/notes`, payload),
  updateNote: (supplierId, noteId, payload) => api.put(`/suppliers/${supplierId}/notes/${noteId}`, payload),
  removeNote: (supplierId, noteId) => api.del(`/suppliers/${supplierId}/notes/${noteId}`),

  // Activity
  listActivity: (supplierId, params = {}) => api.get(`/suppliers/${supplierId}/activity${toQuery(params)}`),
};
